import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ShipperTransportJob {
  id: string;
  order_id: string;
  transporter_company_id: string;
  driver_id: string | null;
  vehicle_id: string | null;
  booked_at: string | null;
  accepted_at: string | null;
  declined_at: string | null;
  created_at: string;
  orders?: any;
  companies?: any;
}

export const useShipperJobs = () => {
  const queryClient = useQueryClient();

  // Récupérer les réservations en attente de confirmation
  const { data: pendingJobs = [], isLoading } = useQuery({
    queryKey: ['shipper-pending-jobs'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Récupérer le company_id de l'utilisateur
      const { data: userData } = await supabase
        .from('users')
        .select('company_id')
        .eq('id', user.id)
        .single();

      const companyId = userData?.company_id;

      // Construire le filtre OR en fonction des données disponibles
      let query = supabase
        .from('transport_jobs')
        .select(`
          *,
          orders!inner (
            id,
            order_number,
            cargo_title,
            cargo_description,
            pickup_city,
            pickup_country,
            delivery_city,
            delivery_country,
            weight_kg,
            trailer_type,
            proposed_price,
            final_price,
            shipper_user_id,
            shipper_company_id
          ),
          companies!transporter_company_id (
            name,
            email,
            phone,
            type,
            address,
            siret
          )
        `)
        .is('accepted_at', null)
        .is('declined_at', null);

      // Appliquer le filtre selon les données disponibles
      if (companyId) {
        query = query.or(`shipper_user_id.eq.${user.id},shipper_company_id.eq.${companyId}`, { foreignTable: 'orders' });
      } else {
        query = query.eq('orders.shipper_user_id', user.id);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      return data as ShipperTransportJob[];
    },
  });

  // Confirmer une réservation
  const confirmJobMutation = useMutation({
    mutationFn: async (jobId: string) => {
      const { data, error } = await supabase
        .from('transport_jobs')
        .update({ accepted_at: new Date().toISOString() })
        .eq('id', jobId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipper-pending-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });

  // Refuser une réservation
  const declineJobMutation = useMutation({
    mutationFn: async (jobId: string) => {
      const { data, error } = await supabase
        .from('transport_jobs')
        .update({ declined_at: new Date().toISOString() })
        .eq('id', jobId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipper-pending-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });

  return {
    pendingJobs,
    isLoading,
    confirmJob: confirmJobMutation.mutateAsync,
    isConfirming: confirmJobMutation.isPending,
    declineJob: declineJobMutation.mutateAsync,
    isDeclining: declineJobMutation.isPending,
  };
};
